import { IconButton } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import {
  FiCalendar,
  FiCopy,
  FiCornerDownRight,
  FiCornerUpLeft,
  FiCornerUpRight,
  FiMail,
  FiMessageSquare,
  FiPhone,
  FiX,
} from 'react-icons/fi'
import Flex from 'typescript-styled-flex'
import {
  EventInfoPosition,
  EventInfoWrapper,
  Header,
  InfoLabel,
  Label,
} from '../../../../components/Calendar/BookingCalendar/EventHint'
import HintPopper from '../../../../components/Calendar/BookingCalendar/EventHint/HintPopper'
import Button from '../../../../components/Template/Button'
import {
  EventRegistration,
  EventStatus,
  IEvent,
} from '../../../../models/Event'
import mapEventToFormValues from '../../../../models/Event/mappers/mapEventToFormValues'
import { ICreateReservationFormValues } from '../../../Form/CreateReservation/CreateReservation'
import CopyReservationModal from '../../CopyReservationModal/CopyReservationModal'
import { TransferReservationModal } from '../../TransferReservationModal'
import TemplateSendModal from '../../ReservationModal/TemplateSendModal'
import { Modals } from '../../../Modals/ModalContainer'
import useSize from '../../../../hooks/useSize'
import EventStatusExpander, {
  IEventStatusExpanderProps,
} from './EventStatusExpander'
import useFormatDate from '../../../../hooks/useFormatDate'
import useIsScope from '../../../../hooks/useIsScope'
import { PermissionScope } from '../../../Employees'

function EventHint({
  open,
  anchorEvent,
  close,
  editEvent,
  statusSettings,
  event,
  updateEvent,
  modalOpen,
  modalClose,
  copyModalOpen,
  transferModalOpen,
}: IEventHintProps & IEventHintState & IEventHintDispatch) {
  const [statusMenuOpen, setStatusMenuOpen] = useState(false)
  const eventData = event ? mapEventToFormValues(event) : null
  const { t } = useTranslation()

  const size = useSize('calendar')
  const registration = event && String(event.registration)

  const scope = useIsScope({
    reservation: [PermissionScope.SCOPE_CAN_VIEW_RESERVATIONS],
    reservationEdit: [PermissionScope.SCOPE_CAN_EDIT_RESERVATIONS],
  })

  useEffect(() => {
    if (open && !copyModalOpen && !transferModalOpen) {
      window.addEventListener('wheel', close)
    } else {
      window.removeEventListener('wheel', close)
    }

    return () => window.removeEventListener('wheel', close)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, copyModalOpen, transferModalOpen])

  useEffect(() => {
    if (open) {
      setStatusMenuOpen(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  function handleEditEventClick() {
    if (editEvent) {
      editEvent(event.id)()
    }
  }

  const CopyModal = (
    <CopyReservationModal
      eventData={eventData}
      eventId={event && event.id}
      close={modalClose}
      onCopySuccess={close}
    />
  )

  const TransferModal = (
    <TransferReservationModal
      close={modalClose}
      onTransferSuccess={close}
      eventData={eventData}
      eventId={event && event.id}
    />
  )

  function preventPropagation(e) {
    e.stopPropagation()
  }

  const toggleCopyModal = () => modalOpen(Modals.CopyModal, CopyModal)

  const toggleTransferModal = () =>
    modalOpen(Modals.TransferModal, TransferModal)

  const updateStatus = (status: EventStatus) => {
    const mappedEvent = {
      ...eventData,
      reservationStatus: status,
    }
    updateEvent(mappedEvent, event.id)
  }

  const updateRegistration = (registration: EventRegistration) => () => {
    const mappedEvent = {
      ...eventData,
      registration,
    }
    updateEvent(mappedEvent, event.id)
  }

  const preventDefault = e => {
    e.preventDefault()
    e.stopPropagation()
  }

  const formatDate = useFormatDate()

  return anchorEvent && event ? (
    <HintPopper
      size={size}
      open={open}
      anchorEl={anchorEvent}
      onMouseMoveCapture={preventDefault}
    >
      <div onClick={preventPropagation}>
        <Header size={size}>
          <Label size={size}>
            {event.guest.name ? event.guest.name : event.id}
          </Label>

          {!(event.airbnbId && event.airbnbId !== '0') &&
            scope.reservation &&
            scope.reservationEdit && (
              <IconButton
                style={{ padding: `${12 * size}px` }}
                onClick={toggleTransferModal()}
              >
                <FiCornerUpRight size={`${18 * size}px`} />
              </IconButton>
            )}

          {scope.reservation && scope.reservationEdit && (
            <IconButton
              style={{ padding: `${12 * size}px` }}
              onClick={toggleCopyModal()}
            >
              <FiCopy size={`${18 * size}px`} />
            </IconButton>
          )}
        </Header>

        <EventStatusExpander
          disableExpand={!scope.reservation || !scope.reservationEdit}
          size={size}
          event={event}
          statusSettings={statusSettings}
          currentStatus={event.stage}
          isOpen={statusMenuOpen}
          toggleOpen={setStatusMenuOpen}
          updateStatus={updateStatus}
        />

        <EventInfoWrapper size={size}>
          <EventInfoPosition size={size}>
            <Flex>
              <FiCalendar size={18 * size} style={{ opacity: 0.3 }} />
            </Flex>
            <InfoLabel size={size}>
              {formatDate(event.startDate)} - {formatDate(event.finishDate)}
            </InfoLabel>
          </EventInfoPosition>
          {event.guest.phone && (
            <EventInfoPosition size={size}>
              <Flex>
                <FiPhone size={18 * size} style={{ opacity: 0.3 }} />
              </Flex>
              <InfoLabel size={size}>{event.guest.phone}</InfoLabel>
              <Flex>
                <Button
                  size={size}
                  light={true}
                  style={{ marginLeft: `${10 * size}px` }}
                  href={`tel:${event.guest.phone}`}
                >
                  <FiPhone />
                </Button>
                <Button
                  size={size}
                  light={true}
                  style={{ marginLeft: `${10 * size}px` }}
                  onClick={modalOpen(
                    Modals.TemplateSendModal,
                    <TemplateSendModal
                      email={event.guest.phone}
                      event={event}
                      type={'sms'}
                    />,
                  )}
                >
                  <FiMessageSquare />
                </Button>
              </Flex>
            </EventInfoPosition>
          )}
          {event.guest.email && (
            <EventInfoPosition size={size}>
              <Flex>
                <FiMail size={18 * size} style={{ opacity: 0.3 }} />
              </Flex>
              <InfoLabel size={size}>{event.guest.email}</InfoLabel>
              <Flex>
                <Button
                  size={size}
                  light={true}
                  style={{ marginLeft: `${10 * size}px` }}
                  onClick={modalOpen(
                    Modals.TemplateSendModal,
                    <TemplateSendModal
                      email={event.guest.email}
                      event={event}
                    />,
                  )}
                >
                  <FiMail />
                </Button>
              </Flex>
            </EventInfoPosition>
          )}
          {String(registration) === '0' && (
            <EventInfoPosition size={size}>
              <Flex>
                <FiX size={18 * size} style={{ opacity: 0.3 }} />
              </Flex>
              {/*BBO_1405_no_registration*/}
              <InfoLabel size={size}>{t('reservation_registration')}</InfoLabel>
              {scope.reservation && scope.reservationEdit && (
                <Flex>
                  <Button
                    size={size}
                    light={true}
                    style={{ marginLeft: `${10 * size}px` }}
                    onClick={updateRegistration('1')}
                  >
                    <FiCornerDownRight />
                  </Button>
                </Flex>
              )}
            </EventInfoPosition>
          )}
          {String(registration) === '1' && (
            <EventInfoPosition size={size}>
              <Flex>
                <FiCornerDownRight size={18 * size} style={{ opacity: 0.3 }} />
              </Flex>
              {/*BBO_1405_checkin*/}
              <InfoLabel size={size}>
                {t('reservation_registration_checked_in')}
              </InfoLabel>
              {scope.reservation && scope.reservationEdit && (
                <Flex>
                  <Button
                    size={size}
                    light={true}
                    style={{ marginLeft: `${10 * size}px` }}
                    onClick={updateRegistration('2')}
                  >
                    <FiCornerUpLeft />
                  </Button>
                </Flex>
              )}
            </EventInfoPosition>
          )}
          {String(registration) === '2' && (
            <EventInfoPosition size={size}>
              <Flex>
                <FiCornerUpLeft size={18 * size} style={{ opacity: 0.3 }} />
              </Flex>
              {/*BBO_1405_checkout*/}
              <InfoLabel size={size}>
                {t('reservation_registration_checked_out')}
              </InfoLabel>
              {scope.reservation && scope.reservationEdit && (
                <Flex>
                  <Button
                    size={size}
                    light={true}
                    style={{ marginLeft: `${10 * size}px` }}
                    onClick={updateRegistration('1')}
                  >
                    <FiCornerDownRight />
                  </Button>
                </Flex>
              )}
            </EventInfoPosition>
          )}
        </EventInfoWrapper>
        {scope.reservation && (
          <Button
            size={size}
            onClick={handleEditEventClick}
            primary={true}
            style={{ height: `${60 * size}px` }}
          >
            <Trans i18nKey={'z677_open_reservation'} />
          </Button>
        )}
      </div>
    </HintPopper>
  ) : (
    <div />
  )
}

export default EventHint

export interface IEventHintProps {
  anchorEvent: any
  open: boolean
  eventId: IEvent['id']
  copyModalOpen?: boolean
  transferModalOpen?: boolean
  close?: () => void
}

export interface IEventHintState {
  statusSettings: IEventStatusExpanderProps['statusSettings']
  event: IEvent
}

export interface IEventHintDispatch {
  editEvent?: (id: string) => () => void
  modalOpen: (id, element) => () => void
  modalClose: (id) => void
  updateEvent: (
    event: ICreateReservationFormValues,
    eventId: IEvent['id'],
  ) => void
}
